// frontend/components/doc/IconPickerModal.tsx

import { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import koData from "../../constants/emoji-ko.json";
import twitterData from "@emoji-mart/data/sets/14/twitter.json";
import i18n from "@emoji-mart/data/i18n/ko.json";
import Picker from "@emoji-mart/react";
import * as Lucide from "lucide-react";
import { icons } from "lucide-react";
import { useSession } from "next-auth/react";
import { API_URL } from "../../utils/api";
import { getAttachmentUrl } from "../../utils/attachmentUrl";

if (!(twitterData as any).koMapped) {
    const baseEmojis = (twitterData as any).emojis;
    const koEmojis = (koData as any).emojis;
    if (baseEmojis && koEmojis) {
        for (const id in baseEmojis) {
            if (koEmojis[id]) {
                baseEmojis[id].name = koEmojis[id].name;
                baseEmojis[id].keywords = koEmojis[id].keywords;
            }
        }
    }
    (twitterData as any).koMapped = true;
}

interface IconPickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (value: string | null) => void;
    documentId?: string;
    align?: "left" | "right";
    position?: "top" | "bottom";
    isTextEmojiMode?: boolean;
    closeOnSelect?: boolean;
}

const getUploadedEmojiHistory = (userId: string): string[] => {
    if (typeof window === "undefined") return [];
    try {
        const data = localStorage.getItem(`alpha-custom-emojis-${userId}`);
        return data ? JSON.parse(data) : [];
    } catch {
        return [];
    }
};

const saveUploadedEmojiToHistory = (userId: string, url: string) => {
    if (typeof window === "undefined" || !url) return;
    try {
        const history = getUploadedEmojiHistory(userId);
        if (!history.includes(url)) {
            const newHistory = [url, ...history].slice(0, 36);
            localStorage.setItem(`alpha-custom-emojis-${userId}`, JSON.stringify(newHistory));
        }
    } catch (e) {
        console.error(e);
    }
};

// 노션 스타일 10가지 프리셋 색상 (다크/라이트 호환)
const COLOR_PRESETS = [
    { name: "기본", value: "currentColor", bg: "bg-gray-300 dark:bg-gray-600" },
    { name: "회색", value: "#9CA3AF", bg: "bg-[#9CA3AF]" },
    { name: "갈색", value: "#A16207", bg: "bg-[#A16207]" },
    { name: "주황색", value: "#EA580C", bg: "bg-[#EA580C]" },
    { name: "노란색", value: "#CA8A04", bg: "bg-[#CA8A04]" },
    { name: "초록색", value: "#16A34A", bg: "bg-[#16A34A]" },
    { name: "파란색", value: "#2563EB", bg: "bg-[#2563EB]" },
    { name: "보라색", value: "#9333EA", bg: "bg-[#9333EA]" },
    { name: "분홍색", value: "#DB2777", bg: "bg-[#DB2777]" },
    { name: "빨간색", value: "#DC2626", bg: "bg-[#DC2626]" }
];

const useSafeLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function IconPickerModal({ isOpen, onClose, onSelect, documentId, align = "left", position, isTextEmojiMode = false, closeOnSelect = true }: IconPickerModalProps) {
    const { data: session } = useSession();
    const accessToken = (session?.user as any)?.accessToken;

    const modalRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState<"emoji" | "icon" | "upload">("emoji");
    const [iconColor, setIconColor] = useState<string>("currentColor");
    const [searchQuery, setSearchQuery] = useState("");
    const [showColorPalette, setShowColorPalette] = useState(false);

    // Lucide 전체 아이콘 리스트 추출 (Lucide 속성 등 불필요한 객체 제외)
    const allIconNames = useMemo(() => {
        return Object.keys(icons).filter(key => typeof (icons as any)[key] === 'function' || typeof (icons as any)[key] === 'object');
    }, []);

    // 아이콘 검색 필터링
    const filteredIcons = useMemo(() => {
        if (!searchQuery) return allIconNames;
        return allIconNames.filter(name => name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [allIconNames, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClose]);

    const [computedPosition, setComputedPosition] = useState<"top" | "bottom">("bottom");

    useSafeLayoutEffect(() => {
        if (position) {
            setComputedPosition(position);
            return;
        }
        if (!isOpen || !modalRef.current) return;

        const adjustPosition = () => {
            const modalEl = modalRef.current;
            if (!modalEl) return;
            const parent = modalEl.parentElement;
            if (!parent) return;
            const parentRect = parent.getBoundingClientRect();
            const modalHeight = modalEl.offsetHeight || 480;

            const spaceBelow = window.innerHeight - parentRect.bottom;
            const spaceAbove = parentRect.top;

            if (spaceBelow >= modalHeight) {
                setComputedPosition("bottom");
            } else if (spaceAbove > spaceBelow) {
                setComputedPosition("top");
            } else {
                setComputedPosition("bottom");
            }
        };

        adjustPosition();
        window.addEventListener("resize", adjustPosition);
        return () => window.removeEventListener("resize", adjustPosition);
    }, [isOpen, position]);

    if (!isOpen) return null;

    const handleRandomEmoji = () => {
        const baseEmojis = (twitterData as any).emojis;
        const emojiKeys = Object.keys(baseEmojis);
        const randomKey = emojiKeys[Math.floor(Math.random() * emojiKeys.length)];
        const emojiObj = baseEmojis[randomKey];
        const unified = emojiObj?.skins?.[0]?.unified || emojiObj?.id;
        if (unified) {
            onSelect(`emoji:${unified}`);
            if (closeOnSelect) {
                onClose();
            }
        }
    };

    // 랜덤 아이콘 뽑기
    const handleRandomIcon = () => {
        const randomName = allIconNames[Math.floor(Math.random() * allIconNames.length)];
        const randomColor = COLOR_PRESETS[Math.floor(Math.random() * COLOR_PRESETS.length)].value;
        setIconColor(randomColor);
        onSelect(`lucide:${randomName}${randomColor !== 'currentColor' ? `:${randomColor}` : ''}`);
        if (closeOnSelect) {
            onClose();
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            alert("이미지 파일 크기는 최대 5MB로 제한됩니다.");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("file", file);
            if (documentId) formData.append("documentId", documentId);
            formData.append("purpose", "document");

            const headers: Record<string, string> = {};
            if (accessToken) {
                headers["Authorization"] = `Bearer ${accessToken}`;
            }

            const res = await fetch(`${API_URL}/upload/file`, {
                method: "POST",
                credentials: "include",
                headers,
                body: formData,
            });

            if (res.ok) {
                const data = await res.json();
                const userId = (session?.user as any)?.id || "guest";
                saveUploadedEmojiToHistory(userId, data.url);
                onSelect(`upload:${data.url}`);
                if (closeOnSelect && !isTextEmojiMode) {
                    onClose();
                }
            } else {
                const errData = await res.json().catch(() => ({}));
                alert(errData.error || "이미지 업로드에 실패했습니다.");
            }
        } catch (err) {
            console.error("아이콘 이미지 업로드 에러:", err);
            alert("이미지 업로드 중 오류가 발생했습니다.");
        }
    };

    const posClass = computedPosition === "bottom" ? "top-full mt-2" : "bottom-full mb-2";
    const alignClass = align === "left" ? "left-0" : "right-0";

    return (
        <div ref={modalRef} className={`absolute ${posClass} ${alignClass} z-60 shadow-2xl rounded-lg bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 w-88.5 overflow-hidden text-gray-800 dark:text-gray-200`}>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-zinc-800 px-2">
                <div className="flex text-sm font-medium space-x-1">
                    <button onClick={() => setActiveTab("emoji")} className={`px-4 py-2.5 transition-colors ${activeTab === "emoji" ? "border-b-2 border-blue-500 text-blue-500 font-bold" : "hover:bg-gray-50 dark:hover:bg-zinc-800"}`}>
                        이모지
                    </button>
                    <button onClick={() => setActiveTab("icon")} className={`px-4 py-2.5 transition-colors ${activeTab === "icon" ? "border-b-2 border-blue-500 text-blue-500 font-bold" : "hover:bg-gray-50 dark:hover:bg-zinc-800"}`}>
                        아이콘
                    </button>
                    <button onClick={() => setActiveTab("upload")} className={`px-4 py-2.5 transition-colors ${activeTab === "upload" ? "border-b-2 border-blue-500 text-blue-500 font-bold" : "hover:bg-gray-50 dark:hover:bg-zinc-800"}`}>
                        업로드
                    </button>
                </div>
                <button onClick={() => { onSelect(null); onClose(); }} className="text-xs font-medium text-gray-400 hover:text-red-500 transition-colors px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800">
                    제거
                </button>
            </div>

            <div className="relative overflow-hidden">
                {activeTab === "emoji" && (
                    <div className="relative">
                        <Picker
                            data={twitterData}
                            i18n={i18n}
                            onEmojiSelect={(emoji: any) => {
                                if (isTextEmojiMode) {
                                    onSelect(emoji.native);
                                } else {
                                    onSelect(`emoji:${emoji.unified}`);
                                    if (closeOnSelect) {
                                        onClose();
                                    }
                                }
                            }}
                            set="twitter"
                            theme={document.documentElement.classList.contains("dark") ? "dark" : "light"}
                            previewPosition="none"
                            skinPosition="none"
                            emojiSize={28}
                            emojiButtonSize={36}
                        />
                        {/* 이모지 랜덤 셔플 버튼 (Picker 헤더 우측에 절대 위치로 오버레이) */}
                        <button onClick={handleRandomEmoji} title="랜덤 이모지" className="absolute top-14.5 right-14.5 p-1.5 rounded hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-500 dark:text-gray-400 transition-colors z-10 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-sm shadow-sm">
                            <Lucide.Dices size={16} />
                        </button>
                    </div>
                )}

                {activeTab === "icon" && (
                    <div className="p-3 flex flex-col h-108.75">
                        <div className="flex items-center space-x-1.5 mb-3 relative">
                            <div className="relative flex-1">
                                <Lucide.Search className="absolute left-2.5 top-1.5 text-gray-400" size={14} />
                                <input
                                    type="text"
                                    placeholder="Search (English)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1 bg-gray-100 dark:bg-zinc-800 rounded outline-none text-sm focus:ring-1 focus:ring-blue-500"
                                />
                            </div>
                            <button onClick={() => setShowColorPalette(!showColorPalette)} title="색상 팔레트" className="p-1 rounded hover:bg-gray-200 dark:hover:bg-zinc-700 transition-colors border border-transparent dark:border-zinc-700">
                                <div className={`w-4 h-4 rounded-full ${COLOR_PRESETS.find(p => p.value === iconColor)?.bg || 'bg-gray-300'}`} />
                            </button>
                            <button onClick={handleRandomIcon} title="랜덤 아이콘" className="p-1 rounded hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-500 dark:text-gray-400 transition-colors">
                                <Lucide.Dices size={16} />
                            </button>

                            {showColorPalette && (
                                <div className="absolute top-full right-0 mt-1.5 p-2.5 bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-xl z-20 flex flex-wrap gap-1.5 w-50">
                                    {COLOR_PRESETS.map((preset) => (
                                        <button
                                            key={preset.value}
                                            onClick={() => { setIconColor(preset.value); setShowColorPalette(false); }}
                                            title={preset.name}
                                            className={`w-6 h-6 rounded-full ${preset.bg} ${iconColor === preset.value ? 'ring-2 ring-blue-500 ring-offset-2 dark:ring-offset-zinc-800' : 'hover:scale-110'} transition-transform`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 무제한 아이콘 그리드 */}
                        <div className="flex-1 overflow-y-auto pr-1 custom-scrollbar">
                            <div className="grid grid-cols-9 gap-1">
                                {filteredIcons.map((iconName) => {
                                    const IconComponent = (Lucide as any)[iconName];
                                    if (!IconComponent) return null;
                                    return (
                                        <button
                                            key={iconName}
                                            onClick={() => {
                                                onSelect(`lucide:${iconName}${iconColor !== 'currentColor' ? `:${iconColor}` : ''}`);
                                                if (closeOnSelect) {
                                                    onClose();
                                                }
                                            }}
                                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-zinc-800 flex items-center justify-center transition-colors"
                                            title={iconName}
                                        >
                                            <IconComponent
                                                size={20}
                                                color={iconColor === 'currentColor' ? undefined : iconColor}
                                                className={iconColor === 'currentColor' ? "text-gray-600 dark:text-gray-300" : ""}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                            {filteredIcons.length === 0 && (
                                <div className="text-center text-sm text-gray-400 mt-10">검색 결과가 없습니다.</div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "upload" && (
                    <div className="p-4 flex flex-col h-108.75 overflow-y-auto">
                        {/* 최근 업로드한 커스텀 이모지 섹션 */}
                        {(() => {
                            const userId = (session?.user as any)?.id || "guest";
                            const history = getUploadedEmojiHistory(userId);
                            if (history.length === 0) return null;
                            return (
                                <div className="mb-4">
                                    <h3 className="text-xs font-semibold text-gray-500 dark:text-zinc-400 mb-2 text-left">최근 업로드한 이모지</h3>
                                    <div className="grid grid-cols-6 gap-2 bg-gray-50 dark:bg-zinc-800/40 p-2 rounded-lg border border-gray-200 dark:border-zinc-800/60 max-h-40 overflow-y-auto">
                                        {history.map((url, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => {
                                                    onSelect(`upload:${url}`);
                                                    if (closeOnSelect && !isTextEmojiMode) {
                                                        onClose();
                                                    }
                                                }}
                                                className="w-10 h-10 rounded border border-gray-200 dark:border-zinc-700 hover:border-blue-500 dark:hover:border-blue-400 p-1 flex items-center justify-center bg-white dark:bg-zinc-900 transition-colors"
                                            >
                                                <img src={getAttachmentUrl(url)} alt="custom emoji" className="w-full h-full object-contain" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 업로드 박스 */}
                        <div className="flex-1 flex flex-col justify-center">
                            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-zinc-700 rounded-lg p-6 cursor-pointer hover:border-gray-400 dark:hover:border-zinc-500 hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-all">
                                <Lucide.UploadCloud className="h-8 w-8 text-gray-400 mb-2" />
                                <span className="text-xs font-bold text-gray-600 dark:text-gray-300 mb-1">클릭하여 이미지 업로드</span>
                                <span className="text-[10px] text-gray-400">최대 5MB, 투명 배경(PNG/GIF) 권장</span>
                                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                            </label>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
